import { Download, ExternalLink, Link2, PackagePlus } from 'lucide-react'
import { toast } from 'sonner'

import { useState } from 'react'

import { cn } from '@/lib/utils'
import { resolveMediaUrl } from '@/lib/media/resolveUrl'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'

import { assistantText } from './assistantText'
import { useAssistant } from './AssistantProvider'

const fileNameFor = (name: string, url: string) => {
  const ext = url.split('?')[0].split('.').pop()
  const base = name.replace(/[^\p{L}\p{N} _-]/gu, '').trim().replace(/\s+/g, '-') || 'image'
  return ext && ext.length <= 5 ? `${base}.${ext}` : base
}

interface ImageActionsProps {
  name: string
  path: string
  mediaId?: string
  className?: string
}

/** Download, open, copy link, and start a product from a generated image. */
export const ImageActions = ({ name, path, mediaId, className }: ImageActionsProps) => {
  const { language, capabilities, send, open } = useAssistant()
  const t = assistantText(language)
  const [downloading, setDownloading] = useState(false)
  const url = resolveMediaUrl(path)

  const download = async () => {
    setDownloading(true)
    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const blob = await res.blob()
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = href
      a.download = fileNameFor(name, url)
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(href)
    } catch (error) {
      // The CDN may not allow cross-origin reads; opening the file still lets the merchant save it.
      console.warn('Image download fell back to opening it', error)
      window.open(url, '_blank', 'noopener')
    } finally {
      setDownloading(false)
    }
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url)
      toast.success(t.linkCopied)
    } catch (error) {
      console.warn('Copy failed', error)
    }
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      <Button variant="secondary" size="sm" onClick={download} disabled={downloading} className="gap-1">
        {downloading ? <Spinner className="size-3.5" /> : <Download />} {t.download}
      </Button>
      <Button variant="ghost" size="icon-sm" onClick={() => window.open(url, '_blank', 'noopener')} aria-label={t.openImage} title={t.openImage}>
        <ExternalLink />
      </Button>
      <Button variant="ghost" size="icon-sm" onClick={copyLink} aria-label={t.copyLink} title={t.copyLink}>
        <Link2 />
      </Button>
      {mediaId && capabilities?.tools.includes('propose_create_product') && (
        <Button
          variant="ghost"
          size="sm"
          className="gap-1"
          onClick={() => {
            open()
            send(language === 'bn'
              ? `এই ছবি দিয়ে একটি পণ্যের লিস্টিং তৈরি করুন (thumbnail media id: ${mediaId})`
              : `Create a product listing using this image as the thumbnail (media id: ${mediaId})`)
          }}
        >
          <PackagePlus /> {t.useAsProductImage}
        </Button>
      )}
    </div>
  )
}
